import { Component, ViewChild, Input, Output, EventEmitter, OnChanges, SimpleChanges } from "@angular/core";

import { ILabel } from "@schema/label";
import { LabelService } from "@data/service/label.service";

import { Observable, Subject, merge} from "rxjs";
import { debounceTime, distinctUntilChanged, filter, map } from "rxjs/operators";
import { NgbTypeahead } from "@ng-bootstrap/ng-bootstrap";

import { sortEntityByName } from "@shared/utility/sort-entity-by-name";
import { IHttpResponse } from "@schema/http";

@Component({
  selector: "app-label-collection",
  templateUrl: "./collection.component.html",
  styleUrls: ["./collection.component.scss"]
})
export class LabelCollectionComponent implements OnChanges {

  /**
   * If the user can edit the label collection
   */
  @Input()
  public isEditable = false;

  /**
   * The list of labels that are currently selected
   */
  @Input()
  public labels: ILabel[] = [];

  /**
   * A list of labels when the user has added or removed a label
   */
  @Output()
  public labelsChange: EventEmitter<ILabel[]> = new EventEmitter();

  /**
   * The typeahead instance
   */
  @ViewChild("instance", { static: true })
  public instance: NgbTypeahead;

  /**
   * Focus on typeahead input
   */
  public focus$ = new Subject<string>();

  /**
   * Click on a typeahead item
   */
  public click$ = new Subject<string>();

  /**
   * If the labels are currently loading in
   */
  public loading = false;

  /**
   * If there was an error loading in the labels
   */
  public error = false;


  /**
   * Get the placeholder for the input when the list is editable
   */
  get inputPlaceholder(): string {
    return (this.labels.length === 0) ? "Select one or more labels" : "";
  }

  /**
   * Cached labels from the API
   */
  protected _labels: ILabel[] = [];

  constructor(
    protected _labelService: LabelService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    // Sort the labels any time we have an updated list of labels
    this.labels = this.labels.sort(sortEntityByName);

    // Load all labels if the user can edit the list
    if (changes.isEditable && changes.isEditable.currentValue) {
      this._loadLabelList();
    }
  }

  /**
   * Show value in typeahead (empty string for labels)
   */
  public labelInputFormatter(item: any): string {
    return "";
  }

  /**
   * Add a label
   */
  public addLabel(label: { item: ILabel }): void {
    // Do nothing when we selected the "no results found" item
    if (label.item === null) { return; }

    // Add the new label to the label list & send it to the parent
    this.labels = [label.item].concat(this.labels);
    this.labelsChange.emit(this.labels);
  }


  /**
   * Remove a lable from the collection
   */
  public removeLabel(labelId: string): void {
    this.labels = this.labels.filter((label: ILabel) => {
      return label.id !== labelId;
    });
    this.labelsChange.emit(this.labels);
  }

  /**
   * Search for a label
   */
  public search = (text$: Observable<string>): Observable<ILabel[]> => {
    // this needs to remain as a property instead of a method due to nb-bootstrap's this binding

    const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());
    const clicksWithClosedPopup$ = this.click$.pipe(filter(() => !this.instance.isPopupOpen()));
    const inputFocus$ = this.focus$;

    // TODO: Add check for loading and push results
    return merge(debouncedText$, inputFocus$, clicksWithClosedPopup$).pipe(
      debounceTime(300),
      distinctUntilChanged(),
      map(term => {
        // Remove already selected labels
        let labels = this._labels.filter(labelA => !this.labels.find(labelB => labelA.id === labelB.id));

        // Filter via the search term
        if (term) {
          labels = labels.filter(label => label.name.toLowerCase().indexOf(term) !== -1);
        }

        if (labels.length === 0) {
          labels = [null];
        }

        // Return the filtered labels
        return labels;
      }),
    );
  }

  protected _loadLabelList(): void {
    this.loading = true;
    this.error = false;
    this._labelService
      .all({ pageSize: 100, retryMaxAttempts: 1 })
      .subscribe({
        next: (response: IHttpResponse<ILabel[]>) => {
          this._labels = response.data;
        },
        error: () => {
          this._labels = [];
          this.error = true;
        },
        complete: () => {
          this.loading = false;
        }
      });
  }

}
